import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, InputAdornment, TextField, Typography, makeStyles } from '@material-ui/core';
import { addChildNode, getNodeById, saveNodeById } from '../../api/node';
import { NodeStatus, Node, TitleTree, User, UserRole, NodeTypeName } from '../../domain/domain';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../reducers';
import { UiActionType } from '../../reducers/ui.reducer';
import { stripTags } from '../../utils';
import {
    findSubTreeByNodeId,
    getNodeTypeNameText,
    isBelowSection,
    isCitationValid,
    getCitationErrorMessage,
    doesNotRequireCitation
} from './utils';
import { ComponentManagementActionType } from '../../reducers/comp.actions';
import DMP from './DMP';

const useStyles = makeStyles(theme => ({
    diffCSS: {
        borderTop: '1px solid #ddd',
        fontSize: '12px',
        marginTop: '10px',
        paddingTop: 10,
        minHeight: 50,
        overflow: 'auto',
    },
}));

const AddEditNodeDialog: React.FC = () => {
    const open = useSelector((app: ApplicationState) => app.comp.addEditNodeDialogOpen);
    const mode = useSelector((app: ApplicationState) => app.comp.addEditNodeDialogMode);
    const subtree = useSelector((app: ApplicationState) => app.comp.addEditNodeDialogSubTree);
    const grandParentNode = useSelector((app: ApplicationState) => app.comp.addEditNodeDialogGrandParentNode);
    const nodeType = useSelector((app: ApplicationState) => app.comp.addEditNodeDialogNodeType);
    const treeRoot = useSelector((app: ApplicationState) => app.comp.treeRoot);
    const ordinal = useSelector((app: ApplicationState) => app.comp.addEditNodeDialogOrdinal);
    const direction = useSelector((app: ApplicationState) => app.comp.addEditNodeDialogDirection);
    const hasChanges = useSelector((app: ApplicationState) => app.comp.diffChanges);
    const classes = useStyles();
    const [newNode, setNewNode] = useState<Node | undefined>(undefined);
    const [heading, setHeading] = useState<string>("");
    const [parentCitation, setParentCitation] = useState<string>("");
    const [citationEditor, setCitationEditor] = useState<string>("");
    const [originalHeading, setOriginalHeading] = useState<string>("");
    const [originalCitation, setOriginalCitation] = useState<string>("");

    const dispatch = useDispatch();

    let currentUser: User | undefined = useSelector((app: ApplicationState) => app.user.currentUser);

    function getSymbolByType(type: NodeTypeName | undefined): string {
        switch (type) {
            case NodeTypeName.ARTICLE:
            case NodeTypeName.ARTICLE_PART:
            case NodeTypeName.SECTION:
                return '-';
            case NodeTypeName.SUBSECTION:
            case NodeTypeName.PARAGRAPH:
            case NodeTypeName.SUBPARAGRAPH:
            case NodeTypeName.SUB_SUBPARAGRAPH:
                return '(';
            default:
                return '';
        }
    }

    useEffect(() => {
        if (!subtree || !open) {
            return;
        }

        let newCitation = stripTags(getCitation(subtree));

        if (mode === 'add' && grandParentNode && subtree.citation === undefined) {
            let grandParentNodeCitation = stripTags(getCitation(grandParentNode));
            setParentCitation(grandParentNodeCitation + getSymbolByType(nodeType));
            setCitationEditor(newCitation);
        } else {
            setParentCitation(newCitation + getSymbolByType(nodeType));
            setCitationEditor(newCitation);
        }

        let newHeading = stripTags(getHeading(subtree));
        setHeading(newHeading);

        let childNodeType = nodeType;
        let newNode = {
            nodeId: 0,
            parentId: subtree.nodeId,
            status: NodeStatus.CHANGED_NOT_APPROVED,
            nodeType: childNodeType!,
            ordinal: ordinal,
            citationPart: "",
            heading: "",
            content: "",
            metadata: {
                subTreeHasChanges: true,
            },
            citation: "",
            editorialContent: [],
            subTreeHasUnapprovedChanges: false,
            hasUnapprovedEditorialContent: false,
            markedForDeletion: false,
            repealed: false,
            belowEditorialLevel: !isBelowSection(childNodeType)
        };

        setNewNode(newNode);

        setOriginalCitation(newCitation);
        setOriginalHeading(newHeading);

    }, [subtree, nodeType, open, grandParentNode, ordinal, mode]);


    const hasContentChanges = () => {
        return (heading !== originalHeading) || (citationEditor !== originalCitation);
    }

    // Using the utility functions for citation validation
    const isCitationValidInContext = (): boolean => {
        if (!subtree) return false;
        return isCitationValid(citationEditor, subtree.nodeType, treeRoot?.citation);
    };

    function getCitationErrorMessageInContext(): string {
        if (!subtree) return "";
        return getCitationErrorMessage(citationEditor, subtree.nodeType, treeRoot?.citation);
    }

    const onCancel = () => {
        dispatch({ type: ComponentManagementActionType.DIFF_HAS_CHANGES, diffChanges: false });
        dispatch({ type: ComponentManagementActionType.ADD_EDIT_NODE_DIALOG_OPEN, open: false })
    }

    const isFormValid = () => {
        if (!subtree) {
            return false;
        }

        // Check heading requirements
        let requiresHeading = !isBelowSection(subtree.nodeType);
        if ((mode === "add" || mode === "edit") && !heading && requiresHeading) {
            return false;
        }

        // Check citation validity in both modes
        if (mode === "edit" && !doesNotRequireCitation(nodeType!) && !isCitationValidInContext()) {
            return false;
        }

        if (mode === "add" && newNode && !doesNotRequireCitation(nodeType!) &&
            (!newNode.citation || newNode.citation === parentCitation ||
                !isCitationValid(newNode.citation, nodeType!, treeRoot?.citation))) {
            return false;
        }

        if(mode === "edit" && !hasContentChanges()){
            return false;  // Disable save if no change detected.
        }

        return true;
    }

    const getCitation = (tree: TitleTree) => {
        if (tree.subTreeHasUnapprovedChanges) {
            return tree.workingCopyCitation ?? tree.citation;
        }
        return tree.citation
    }

    const getHeading = (tree: TitleTree) => {
        if (tree.subTreeHasUnapprovedChanges) {
            return tree.workingCopyHeading ?? tree.heading;
        }
        return tree.heading;
    }

    const onSave = () => {
        if (!subtree) { return; }

        if (!isCitationValidInContext()) {
            dispatch({
                type: UiActionType.SNACKBAR_STATE_CHANGED,
                newState: {
                    open: true,
                    message: getCitationErrorMessageInContext(),
                    severity: "error"
                }
            });
            return;
        }

        if (mode === "edit") {
            getNodeById(subtree.nodeId).then(response => {
                let node = response.data;
                if (!node.nodeHistory) {
                    node.nodeHistory = {
                        heading: heading,
                        content: "",
                        citation: citationEditor,
                    }
                } else {
                    node.nodeHistory = { ...node.nodeHistory, heading: heading, citation: citationEditor }
                }
                saveNodeById(node).then(response => {
                    dispatch({
                        type: ComponentManagementActionType.SUBTREE_TITLE_CHANGED,
                        nodeId: response.data.nodeId,
                        newHeading: response.data.heading,
                        newWorkingCopyHeading: response.data.nodeHistory?.heading,
                        newCitation: response.data.citation,
                        newWorkingCopyCitation: response.data.nodeHistory?.citation,
                        repealed: response.data.repealed,
                        newCitationPart: response.data.citationPart
                    })
                    dispatch({ type: ComponentManagementActionType.EDITOR_NODE_UPDATED, editorNode: response.data });
                    if (response.data.heading !== response.data.nodeHistory?.heading) {
                        dispatch({
                            type: ComponentManagementActionType.HEADING_CHANGED,
                            nodeId: response.data.nodeId,
                            headingHasChanges: true,
                        })
                    }
                }).catch(error => {
                    console.error(error);
                })
            }).catch(error => {
                console.error(error);
            })
            dispatch({ type: ComponentManagementActionType.SUBTREE_CHANGED, nodeId: subtree.nodeId, hasChanges: true })
            dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Node updated", severity: "success" } });
            dispatch({ type: ComponentManagementActionType.DIFF_HAS_CHANGES, diffChanges: false });
            dispatch({ type: ComponentManagementActionType.ADD_EDIT_NODE_DIALOG_OPEN, open: false })
        } else {
            if (!newNode) { return; }

            if (!doesNotRequireCitation(nodeType!) && (!newNode.citation || newNode.citation === parentCitation)) {
                dispatch({
                    type: UiActionType.SNACKBAR_STATE_CHANGED,
                    newState: {
                        open: true,
                        message: "A label for the provision must be entered before the provision is created.",
                        severity: "error"
                    }
                });
                return;
            }

            addChildNode(subtree.nodeId, newNode).then(response => {
                let newChildren = [...subtree.children];
                let node = response.data;
                let newSubTree = {
                    nodeHasChanges: true,
                    heading: node.heading,
                    citation: node.citation,
                    children: [],
                    parentId: node.parentId,
                    subTreeHasUnapprovedChanges: true,
                    workingCopyHasChanges: true,
                    editorialHasChanges: false,
                    headingHasChanges: false,
                    nodeId: node.nodeId,
                    nodeType: node.nodeType,
                    workingCopyHeading: node.heading,
                    workingCopyCitation: node.citation,
                    ordinal: node.ordinal ?? subtree.children.length,
                    citationPart: node.citationPart,
                    repealed: node.repealed,
                    belowEditorialLevel: node.belowEditorialLevel,
                }
                let existingSubTree = findSubTreeByNodeId(treeRoot!, node.parentId);
                if (existingSubTree === undefined) {
                    return;
                }
                existingSubTree.children = newChildren;
                existingSubTree.children.forEach((childTree) => {
                    let childPosition = childTree.ordinal;
                    if (childPosition >= node.ordinal!) {
                        childTree.ordinal = childPosition + 1
                    }
                })
                newChildren.push(newSubTree);

                dispatch({ type: ComponentManagementActionType.TREE_ROOT_UPDATED, treeRoot: { ...treeRoot } });
                dispatch({ type: ComponentManagementActionType.SUBTREE_CHANGED, nodeId: subtree.nodeId, hasChanges: true });
                dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Node Added", severity: "success" } });

            }).catch(error => {
                console.error(error);
                dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Could not add new node", severity: "error" } });
            })
            dispatch({ type: ComponentManagementActionType.DIFF_HAS_CHANGES, diffChanges: false });
            dispatch({ type: ComponentManagementActionType.ADD_EDIT_NODE_DIALOG_OPEN, open: false })
        }
    }

    const renderEditModeBody = () => {
        if (!subtree) {
            return <></>
        }
        return (
            <Grid container direction='column' spacing={2}>
                {!doesNotRequireCitation(subtree.nodeType) &&
                    <Grid item style={{ width: '100%' }}>
                        <Typography variant="h2" gutterBottom>Citation</Typography>
                        <TextField
                            value={citationEditor}
                            fullWidth={true}
                            onChange={(event) => {
                                dispatch({ type: ComponentManagementActionType.DIFF_HAS_CHANGES, diffChanges: true });
                                setCitationEditor(event.target.value);
                            }}
                            variant='outlined'
                            size='small'
                            disabled={currentUser?.userRole === UserRole.NON_PUBLICATIONS_TEAM}
                            error={!isCitationValidInContext()}
                            helperText={!isCitationValidInContext() ? getCitationErrorMessageInContext() : ""}
                        />
                        {(hasChanges || subtree.nodeHasChanges) &&
                            <div className={classes.diffCSS}>
                                <h1 style={{ fontWeight: 'bold', textDecoration: 'underline', paddingBottom: '10px' }}>Revisions:</h1>
                                <DMP string1={subtree.citation} string2={citationEditor} />
                            </div>
                        }
                    </Grid>
                }
                <Grid item> </Grid>
                <Grid item>
                </Grid>
                <Grid item style={{ width: '100%' }}>
                    <Typography variant="h2" gutterBottom>Heading</Typography>
                    <TextField
                        autoFocus
                        value={heading}
                        fullWidth={true}
                        multiline={true}
                        onChange={(event) => {
                            dispatch({ type: ComponentManagementActionType.DIFF_HAS_CHANGES, diffChanges: true });
                            setHeading(event.target.value)
                        }}
                        variant='outlined'
                        size='small'
                    />
                    {(hasChanges || subtree.nodeHasChanges) &&
                        <div className={classes.diffCSS}>
                            <h1 style={{ fontWeight: 'bold', textDecoration: 'underline', paddingBottom: '10px' }}>Revisions:</h1>
                            <DMP string1={subtree.heading} string2={heading} />
                        </div>
                    }
                </Grid>
            </Grid>
        )
    }

    const renderAddModeBody = () => {
        return (
            <Grid container direction='column' spacing={2}>
                {!doesNotRequireCitation(nodeType!) &&
                    <>
                        <Grid item style={{ width: '100%' }}>
                            <Typography variant="h2" gutterBottom>Citation</Typography>
                            <TextField
                                fullWidth={true}
                                autoFocus={true}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start" component="div">
                                        <div>{parentCitation}</div>
                                    </InputAdornment>,
                                }}
                                onChange={(event) => {
                                    if (!newNode) {
                                        return;
                                    }
                                    const citationValue = event.target.value;
                                    const fullCitation = parentCitation + citationValue;
                                    setNewNode({ ...newNode, citation: fullCitation });
                                }}
                                variant='outlined'
                                size='small'
                                error={newNode && !isCitationValid(newNode.citation, nodeType!, treeRoot?.citation)}
                                helperText={newNode && !isCitationValid(newNode.citation, nodeType!, treeRoot?.citation) ?
                                    getCitationErrorMessage(newNode.citation, nodeType!, treeRoot?.citation) : ""}
                            />
                        </Grid>
                        <Grid item> </Grid>
                        <Grid item> </Grid>
                    </>
                }
                <Grid item style={{ width: '100%' }}>
                    <Typography variant="h2" gutterBottom>Heading</Typography>
                    <TextField
                        fullWidth={true}
                        autoFocus={doesNotRequireCitation(nodeType!)}
                        multiline={true}
                        onChange={(event) => {
                            if (!newNode) {
                                return;
                            }
                            setNewNode({ ...newNode, heading: event.target.value });
                        }}
                        variant='outlined'
                        size='small'
                    />
                </Grid>
            </Grid>
        )
    }

    return (
        <Dialog maxWidth='sm' fullWidth={true} open={open} onClose={() => {
        }}>
            <DialogTitle>
                {mode === "edit" &&
                    <Typography variant="h1" gutterBottom>Edit Node</Typography>
                }
                {mode === "add" &&
                    <Typography variant='h1' gutterBottom>Add {getNodeTypeNameText(nodeType!)} {direction}</Typography>
                }
            </DialogTitle>
            <DialogContent>
                <form onSubmit={(event) => {
                    event.preventDefault();
                    onSave();
                }}>
                    {mode === "add" && renderAddModeBody()}
                    {mode === "edit" && renderEditModeBody()}
                </form>
            </DialogContent>
            <DialogActions>
                <Button size='small' variant='outlined' color='secondary' onClick={onCancel} >Cancel</Button>
                <Button size='small' variant='outlined' color='secondary' onClick={onSave} disabled={!isFormValid()}>Save</Button>
            </DialogActions>
        </Dialog>
    )
}

export default AddEditNodeDialog;